import React, { useState, useEffect, useRef } from 'react';
import { Search, Trash2, FlaskConical, FileText, Upload, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import {
    getPetLabs,
    createLabOrder,
    updateLabOrder,
    deleteLabOrder,
    LabOrder,
    initiateLabAttachmentUpload,
    completeLabAttachmentUpload,
    getLabAttachmentDownloadUrl,
    deleteLabAttachment
} from '../../utils/labOrdersApi';
import { getDiagnosticCatalog, DiagnosticCatalogItem } from '../../utils/labsCatalogApi';
import ConfirmDialog from '../common/ConfirmDialog';

interface PetLabsProps {
    petId: string;
}

const PetLabs: React.FC<PetLabsProps> = ({ petId }) => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [orders, setOrders] = useState<LabOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<DiagnosticCatalogItem[]>([]);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState<LabOrder | null>(null);
    const [showAttachmentDeleteConfirm, setShowAttachmentDeleteConfirm] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState<LabOrder | null>(null);
    const [uploadingOrderId, setUploadingOrderId] = useState<string | null>(null);
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
    const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchOrders();
    }, [petId]);

    // Deep-link focus support: /labs?focus=Heartworm
    useEffect(() => {
        if (loading) return;
        const focus = (searchParams.get('focus') || '').trim().toLowerCase();
        if (!focus || orders.length === 0) return;

        const match = orders.find((o) => ((o.diagnostic?.name || '') as string).toLowerCase().includes(focus));
        if (!match) return;

        setFocusedId(match.id);
        const el = rowRefs.current[match.id];
        if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }

        const t = window.setTimeout(() => setFocusedId(null), 3500);
        return () => window.clearTimeout(t);
    }, [loading, orders, searchParams]);

    // Expose handler for Dashboard to trigger the order lab modal
    useEffect(() => {
        (window as any)._labsOrderHandler = () => {
            setShowSearchModal(true);
        };
        return () => {
            delete (window as any)._labsOrderHandler;
        };
    }, []);

    useEffect(() => {
        if (searchQuery.length > 1 && user?.practice_id && showSearchModal) {
            searchDiagnostics();
        } else {
            setSearchResults([]);
        }
    }, [searchQuery, user?.practice_id, showSearchModal]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const data = await getPetLabs(petId);
            setOrders(data);
        } catch (error) {
            console.error('Failed to fetch labs:', error);
        } finally {
            setLoading(false);
        }
    };

    const searchDiagnostics = async () => {
        if (!user?.practice_id) return;
        try {
            const results = await getDiagnosticCatalog(user.practice_id, searchQuery, true);
            setSearchResults(results);
        } catch (error) {
            console.error('Failed to search diagnostics:', error);
        }
    };

    const handleAddOrder = async (diagnostic: DiagnosticCatalogItem) => {
        try {
            await createLabOrder(petId, {
                diagnostic_id: diagnostic.id,
                quantity: 1,
                status: 'PENDING'
            });
            setSearchQuery('');
            setShowSearchModal(false);
            fetchOrders();
        } catch (error) {
            console.error('Failed to add order:', error);
            alert('Failed to add order');
        }
    };

    const handleUpdateResult = async (order: LabOrder, result: string) => {
        try {
            await updateLabOrder(order.id, {
                result,
                status: 'COMPLETED'
            });
            fetchOrders();
        } catch (error) {
            console.error('Failed to update result:', error);
        }
    };

    const handleUpdateNotes = async (order: LabOrder, notes: string) => {
        try {
            await updateLabOrder(order.id, { result_notes: notes });
        } catch (error) {
            console.error('Failed to update notes:', error);
        }
    };


    const handleDeleteOrder = (order: LabOrder) => {
        setOrderToDelete(order);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteOrder = async () => {
        if (!orderToDelete) return;

        try {
            await deleteLabOrder(orderToDelete.id);
            fetchOrders();
        } catch (error) {
            console.error('Failed to delete order:', error);
            alert('Failed to delete order');
        } finally {
            setShowDeleteConfirm(false);
            setOrderToDelete(null);
        }
    };

    const handleAttachmentUpload = async (order: LabOrder, file: File) => {
        setUploadingOrderId(order.id);
        try {
            // Initiate upload with actual file content type
            const contentType = file.type || 'application/octet-stream';
            const { upload_url, fields, s3_key } = await initiateLabAttachmentUpload(order.id, contentType);

            // Upload to S3
            const formData = new FormData();
            Object.entries(fields).forEach(([key, value]) => {
                formData.append(key, value);
            });
            formData.append('file', file);

            const uploadResponse = await fetch(upload_url, {
                method: 'POST',
                body: formData
            });

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload file to S3');
            }

            // Complete upload
            await completeLabAttachmentUpload(order.id, s3_key);

            // Refresh orders
            await fetchOrders();
        } catch (error) {
            console.error('Failed to upload attachment:', error);
            alert('Failed to upload attachment');
        } finally {
            setUploadingOrderId(null);
        }
    };

    const handleAttachmentDownload = async (order: LabOrder) => {
        try {
            const downloadUrl = await getLabAttachmentDownloadUrl(order.id);
            window.open(downloadUrl, '_blank');
        } catch (error) {
            console.error('Failed to download attachment:', error);
            alert('Failed to download attachment');
        }
    };

    const handleAttachmentDelete = (order: LabOrder) => {
        setAttachmentToDelete(order);
        setShowAttachmentDeleteConfirm(true);
    };

    const confirmAttachmentDelete = async () => {
        if (!attachmentToDelete) return;

        try {
            await deleteLabAttachment(attachmentToDelete.id);
            await fetchOrders();
        } catch (error) {
            console.error('Failed to delete attachment:', error);
            alert('Failed to delete attachment');
        } finally {
            setShowAttachmentDeleteConfirm(false);
            setAttachmentToDelete(null);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading labs...</div>;

    return (
        <div className="space-y-6">
            {/* Orders Table */}
            <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden">
                {orders.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <FlaskConical className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No lab orders yet.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Date</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Diagnostics</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Result</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-20">Qty</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-32">Encounter</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-center w-20">Upload</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-center w-16">Delete</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {orders.map((order) => (
                                        <tr
                                            key={order.id}
                                            ref={(el) => { rowRefs.current[order.id] = el; }}
                                            className={`hover:bg-gray-50 transition-colors ${focusedId === order.id ? 'bg-yellow-50' : 'bg-white'}`}
                                        >
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                {new Date(order.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="px-4 py-3 font-medium text-gray-900">
                                                {order.diagnostic?.name || 'Unknown Test'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-col gap-2">
                                                    <div className="flex items-center rounded-none overflow-hidden border border-gray-200 w-fit">
                                                        <button
                                                            onClick={() => handleUpdateResult(order, 'POSITIVE')}
                                                            className={`px-3 py-1 text-xs font-bold transition-colors ${order.result === 'POSITIVE'
                                                                ? 'bg-red-600 text-white'
                                                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                                                }`}
                                                        >
                                                            POSITIVE
                                                        </button>
                                                        <button
                                                            onClick={() => handleUpdateResult(order, 'NEGATIVE')}
                                                            className={`px-3 py-1 text-xs font-bold transition-colors ${order.result === 'NEGATIVE'
                                                                ? 'bg-gray-600 text-white'
                                                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                                                }`}
                                                        >
                                                            NEGATIVE
                                                        </button>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        placeholder="Add details..."
                                                        defaultValue={order.result_notes || ''}
                                                        onBlur={(e) => handleUpdateNotes(order, e.target.value)}
                                                        className="text-sm border-gray-200 rounded px-2 py-1 w-full"
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {order.quantity}
                                            </td>
                                            <td className="px-4 py-3">
                                                {order.encounter_id ? (
                                                    <a
                                                        href={`/pet/${petId}/encounter/${order.encounter_id}/intake`}
                                                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-none hover:bg-blue-100 transition-colors whitespace-nowrap"
                                                    >
                                                        {order.encounter_date ? new Date(order.encounter_date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-') : 'Encounter'}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400 text-xs">—</span>
                                                )}
                                            </td>
                                            {/* Upload Column */}
                                            <td className="px-4 py-3 text-center">
                                                {order.result_attachment_s3_key ? (
                                                    <div className="flex items-center justify-center gap-1">
                                                        <button
                                                            onClick={() => handleAttachmentDownload(order)}
                                                            className="text-blue-600 hover:text-blue-700"
                                                            title="Download attachment"
                                                        >
                                                            <FileText className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleAttachmentDelete(order)}
                                                            className="text-gray-400 hover:text-red-500"
                                                            title="Delete attachment"
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <input
                                                            type="file"
                                                            ref={(el) => { fileInputRefs.current[order.id] = el; }}
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file) handleAttachmentUpload(order, file);
                                                            }}
                                                            className="hidden"
                                                            accept=".pdf,.png,.jpg,.jpeg"
                                                        />
                                                        <button
                                                            onClick={() => fileInputRefs.current[order.id]?.click()}
                                                            disabled={uploadingOrderId === order.id}
                                                            className="text-gray-400 hover:text-blue-600 disabled:opacity-50"
                                                            title="Upload attachment"
                                                        >
                                                            {uploadingOrderId === order.id ? (
                                                                <span className="text-xs">...</span>
                                                            ) : (
                                                                <Upload className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                            {/* Delete Column */}
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => handleDeleteOrder(order)}
                                                    className="text-gray-400 hover:text-red-500"
                                                    title="Delete lab order"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Search Lab Modal */}
            {showSearchModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-none shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-900">Order Lab</h2>
                            <button
                                onClick={() => {
                                    setShowSearchModal(false);
                                    setSearchQuery('');
                                    setSearchResults([]);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search for lab test..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-4 pb-4">
                            {searchQuery.length < 2 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <FlaskConical className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                    <p className="text-sm">Type to search lab tests...</p>
                                </div>
                            ) : searchResults.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <p className="text-sm">No lab tests found</p>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {searchResults.map((item) => (
                                        <div
                                            key={item.id}
                                            onClick={() => handleAddOrder(item)}
                                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer rounded-none flex justify-between items-center border border-gray-100"
                                        >
                                            <span className="font-medium text-gray-900">{item.name}</span>
                                            <span className="text-xs text-gray-500">{item.result_type}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && orderToDelete && (
                <ConfirmDialog
                    isOpen={showDeleteConfirm}
                    title="Delete Lab Order"
                    message={`Are you sure you want to delete the order for "${orderToDelete.diagnostic?.name || 'this test'}"?`}
                    confirmText="Delete"
                    onConfirm={confirmDeleteOrder}
                    onClose={() => {
                        setShowDeleteConfirm(false);
                        setOrderToDelete(null);
                    }}
                />
            )}

            {/* Attachment Delete Confirmation Dialog */}
            {showAttachmentDeleteConfirm && attachmentToDelete && (
                <ConfirmDialog
                    isOpen={showAttachmentDeleteConfirm}
                    title="Delete Attachment"
                    message="Are you sure you want to delete this lab result attachment?"
                    confirmText="Delete"
                    onConfirm={confirmAttachmentDelete}
                    onClose={() => {
                        setShowAttachmentDeleteConfirm(false);
                        setAttachmentToDelete(null);
                    }}
                />
            )}
        </div>
    );
};

export default PetLabs;
